import { create } from 'zustand'
import type { GatewayStatus } from '@/lib/gateway'
import type { AuthorizeParams } from '@/lib/applicationsApi'
import { parseAppPath, type ChannelLink } from '@/lib/appLinks'
import { isWideNow, onWideChange } from '@/lib/mobile'
import { initialHistory, pushView, moveView, type ViewHistory } from '@/lib/viewHistory'

export type Theme = 'dark' | 'light'

/** Что открыто: друзья, ЛС или канал сервера. */
export type HomePage = 'friends' | 'inbox' | 'saved' | 'shop' | 'achievements'
export type View = { kind: 'home'; page: HomePage } | { kind: 'home'; channelId: string } | { kind: 'guild'; guildId: string; channelId: string | null } | { kind: 'discovery'; openedAt?: number }

interface AppState {
  moderationOpen: boolean
  openModeration: () => void
  closeModeration: () => void
  theme: Theme
  toggleTheme: () => void
  /** Режим разработчика: показывает «Скопировать ID» в меню. Нужен для ботов и модерации. */
  devMode: boolean
  setDevMode: (on: boolean) => void
  view: View
  navigation: ViewHistory
  goBack: () => void
  goForward: () => void
  resetNavigation: (view?: View) => void
  /** keepPane — не переключать мобильную панель: для автовыбора канала, а не для тапа по нему. */
  setView: (view: View, opts?: { keepPane?: boolean }) => void
  /**
   * Какая панель на экране в мобильной раскладке: навигация (рейка и список) или
   * содержимое (чат, друзья, голос). На широком экране обе видны сразу, и поле не читается.
   * Выбор канала показывает содержимое; стрелка «назад» в шапке возвращает навигацию.
   */
  pane: 'nav' | 'content'
  showNav: () => void
  showContent: () => void
  /** Последний открытый канал каждого сервера. */
  lastChannel: Record<string, string>
  openGuild: (guildId: string, channelId?: string | null) => void
  /** Слой настроек сервера: id сервера и раздел. */
  guildSettings: { guildId: string; section: GuildSettingsSection } | null
  openGuildSettings: (guildId: string, section?: GuildSettingsSection) => void
  closeGuildSettings: () => void
  /** Параметры страницы /oauth2/authorize (добавление бота на сервер) из адресной строки. */
  pendingAuthorize: AuthorizeParams | null
  setPendingAuthorize: (p: AuthorizeParams | null) => void
  /** Код приглашения из адресной строки /invite/<code>. */
  pendingInvite: string | null
  /** Окно «Добавить сервер»: открывается с панели серверов и со стартового экрана. */
  addGuildOpen: boolean
  setAddGuildOpen: (open: boolean) => void
  setPendingInvite: (code: string | null) => void
  showMembers: boolean
  toggleMembers: () => void
  /** Чат голосового канала в правой колонке; подключение к голосу не требуется. */
  voiceChatChannelId: string | null
  setVoiceChatChannelId: (id: string | null) => void
  /** Поиск по серверу: запрос показывает панель результатов вместо участников. */
  searchQuery: string | null
  setSearchQuery: (q: string | null) => void
  /** Поиск в конкретной личной беседе или группе. */
  dmSearch: { channelId: string; query: string } | null
  setDMSearch: (search: AppState['dmSearch']) => void
  /** Панель веток канала в правой колонке. */
  threadsPanel: { guildId: string; channelId: string } | null
  setThreadsPanel: (p: { guildId: string; channelId: string } | null) => void
  /** Сообщение, к которому нужно прокрутить и подсветить после перехода. */
  jumpTarget: { channelId: string; messageId: string } | null
  setJumpTarget: (t: { channelId: string; messageId: string } | null) => void
  /**
   * Ссылка на канал или сообщение, с которой открыли приложение. Проверяется после READY:
   * до него неизвестно, есть ли доступ, и не у чего взять окно вокруг сообщения.
   */
  pendingLink: ChannelLink | null
  setPendingLink: (link: ChannelLink | null) => void
  /** Окно «Нет доступа к этому каналу» после перехода по ссылке. */
  linkDenied: boolean
  setLinkDenied: (on: boolean) => void
  collapsed: Record<string, boolean>
  toggleCategory: (id: string) => void
  gateway: GatewayStatus
  setGateway: (status: GatewayStatus) => void
  currentUser: { id: string; username: string; global_name: string | null } | null
  setCurrentUser: (user: AppState['currentUser']) => void
  /** Слой настроек пользователя поверх приложения. */
  settings: SettingsSection | null
  openSettings: (section?: SettingsSection) => void
  closeSettings: () => void
}

export type SettingsSection = 'profile' | 'account' | 'devices' | 'notifications' | 'voice' | 'keybinds' | 'appearance' | 'premium' | 'applications' | 'desktop' | 'overlay' | 'moderation' | 'releases' | 'help'
export type GuildSettingsSection = 'overview' | 'roles' | 'members' | 'invites' | 'bans' | 'emojis' | 'stickers' | 'soundboard' | 'boost' | 'discovery' | 'webhooks' | 'automod' | 'import' | 'audit'

/** Ссылка авторизации бота: /oauth2/authorize?client_id=…&scope=bot&permissions=…&guild_id=… */
function authorizeFromLocation(): AuthorizeParams | null {
  if (!/^\/oauth2\/authorize\/?$/.test(location.pathname)) return null
  const q = new URLSearchParams(location.search)
  const clientId = q.get('client_id')
  if (!clientId || !/^\d+$/.test(clientId)) return null
  return {
    clientId,
    scope: q.get('scope') ?? 'bot',
    permissions: q.get('permissions') ?? '0',
    guildId: q.get('guild_id'),
    ...(q.get('response_type') ? { responseType: q.get('response_type')! } : {}),
    ...(q.get('redirect_uri') ? { redirectUri: q.get('redirect_uri')! } : {}),
    ...(q.get('state') ? { state: q.get('state')! } : {}),
    ...(q.get('code_challenge') ? { codeChallenge: q.get('code_challenge')! } : {}),
  }
}

/** Возврат из Discord после входа для переноса сервера: /oauth2/discord?code=…&state=… */
export function discordCodeFromLocation(): { code: string; state: string } | null {
  if (!/^\/oauth2\/discord\/?$/.test(location.pathname)) return null
  const q = new URLSearchParams(location.search)
  const code = q.get('code')
  return code ? { code, state: q.get('state') ?? '' } : null
}

/** Ссылки из писем: /verify-email?token=… подтверждает почту, /recover?token=… открывает смену пароля. */
export function emailActionFromLocation(): { kind: 'verify' | 'recover'; token: string } | null {
  const m = /^\/(verify-email|recover)\/?$/.exec(location.pathname)
  const token = new URLSearchParams(location.search).get('token')
  if (!m?.[1] || !token || !/^[A-Za-z0-9_-]{16,128}$/.test(token)) return null
  return { kind: m[1] === 'recover' ? 'recover' : 'verify', token }
}

/** Прямые адреса формы входа: /login и /register открывают её сразу, минуя главную. */
export function authPathFromLocation(): 'login' | 'register' | null {
  if (/^\/login\/?$/.test(location.pathname)) return 'login'
  if (/^\/register\/?$/.test(location.pathname)) return 'register'
  return null
}

/** Публичная витрина без входа: /discovery — каталог, /s/<slug> — карточка сервера. */
export function publicPageFromLocation(): { kind: 'catalog' } | { kind: 'guild'; slug: string } | null {
  if (/^\/discovery\/?$/.test(location.pathname)) return { kind: 'catalog' }
  const m = /^\/s\/([a-z0-9-]{1,64})\/?$/.exec(location.pathname)
  return m?.[1] ? { kind: 'guild', slug: m[1] } : null
}

function inviteFromLocation(): string | null {
  const m = /^\/invite\/([A-Za-z0-9]{2,32})\/?$/.exec(location.pathname)
  return m?.[1] ?? null
}

/**
 * Канал или сообщение из адреса: клик по push-уведомлению, ссылка на сообщение из другого
 * приложения — /channels/@me/{канал}[/{сообщение}] или /channels/{сервер}/{канал}[/{сообщение}].
 */
function linkFromLocation(): ChannelLink | null {
  const link = parseAppPath(location.pathname)
  if (link?.kind !== 'channel') return null
  history.replaceState(null, '', '/')
  return { guildId: link.guildId, channelId: link.channelId, messageId: link.messageId }
}

function viewOf(link: ChannelLink | null): View {
  if (!link) return { kind: 'home', page: 'friends' }
  return link.guildId ? { kind: 'guild', guildId: link.guildId, channelId: link.channelId } : { kind: 'home', channelId: link.channelId }
}


function readDevMode(): boolean {
  try {
    return localStorage.getItem('klichat.dev-mode') === '1'
  } catch {
    return false
  }
}

function readTheme(): Theme {
  try {
    const saved = localStorage.getItem('klichat.theme')
    if (saved === 'light' || saved === 'dark') return saved
  } catch {
    /* приватный режим */
  }
  return 'dark'
}

// адрес читается один раз: linkFromLocation его затирает, и второй вызов уже видит «/»
const initialLink = linkFromLocation()
const initialView = viewOf(initialLink)

export const useAppStore = create<AppState>((set) => {
  const travel = (delta: -1 | 1) => set(s => {
    const navigation = moveView(s.navigation, delta)
    if (navigation === s.navigation) return s
    const view = navigation.entries[navigation.index]!
    return { navigation, view, pane: 'content' as const, searchQuery: null, dmSearch: null,
      threadsPanel: null, jumpTarget: null, pendingLink: null, voiceChatChannelId: null,
      lastChannel: view.kind === 'guild' && view.channelId ? { ...s.lastChannel, [view.guildId]: view.channelId } : s.lastChannel }
  })
  return {
  moderationOpen: false,
  openModeration: () => set({ moderationOpen: true, settings: null }),
  closeModeration: () => set({ moderationOpen: false }),
  theme: readTheme(),
  toggleTheme: () =>
    set((s) => {
      const theme: Theme = s.theme === 'dark' ? 'light' : 'dark'
      try {
        localStorage.setItem('klichat.theme', theme)
      } catch {
        /* приватный режим */
      }
      return { theme }
    }),
  devMode: readDevMode(),
  setDevMode: (on) =>
    set(() => {
      try {
        if (on) localStorage.setItem('klichat.dev-mode', '1')
        else localStorage.removeItem('klichat.dev-mode')
      } catch {
        /* приватный режим */
      }
      return { devMode: on }
    }),
  view: initialView,
  navigation: initialHistory(initialView),
  goBack: () => travel(-1),
  goForward: () => travel(1),
  resetNavigation: (view) => set(s => ({ navigation: initialHistory(view ?? s.view), ...(view ? { view } : {}) })),
  // открылись сразу в канале (push, ссылка) — показываем его, а не список
  pane: 'channelId' in initialView ? 'content' : 'nav',
  showNav: () => set({ pane: 'nav' }),
  showContent: () => set({ pane: 'content' }),
  setView: (view, opts) =>
    set((s) => {
      // выбрали конкретный канал или беседу — на телефоне это значит «покажи его»
      const pane = !opts?.keepPane && 'channelId' in view && view.channelId ? 'content' : s.pane
      const dmSearch = view.kind === 'home' && 'channelId' in view && view.channelId === s.dmSearch?.channelId ? s.dmSearch : null
      const navigation = pushView(s.navigation, view, opts?.keepPane)
      return view.kind === 'guild' && view.channelId ? { view, navigation, pane, dmSearch, lastChannel: { ...s.lastChannel, [view.guildId]: view.channelId } } : { view, navigation, pane, dmSearch }
    }),
  lastChannel: {},
  openGuild: (guildId, channelId) =>
    set((s) => {
      const target = channelId === undefined ? (s.lastChannel[guildId] ?? null) : channelId
      /*
       * Явно названный канал (из панели голоса, из уведомления) показываем; сервер по
       * значку в рейке — нет: на телефоне человек ждёт список каналов, а не последний чат.
       */
      const pane = channelId !== undefined && channelId ? 'content' : s.pane
      const view: View = { kind: 'guild', guildId, channelId: target }
      return { view, navigation: pushView(s.navigation, view), dmSearch: null, pane, lastChannel: target ? { ...s.lastChannel, [guildId]: target } : s.lastChannel }
    }),
  guildSettings: null,
  openGuildSettings: (guildId, section = 'overview') => set({ guildSettings: { guildId, section } }),
  closeGuildSettings: () => set({ guildSettings: null }),
  pendingInvite: inviteFromLocation(),
  setPendingInvite: (pendingInvite) => set({ pendingInvite }),
  addGuildOpen: false,
  setAddGuildOpen: (addGuildOpen) => set({ addGuildOpen }),
  pendingAuthorize: authorizeFromLocation(),
  setPendingAuthorize: (pendingAuthorize) => set({ pendingAuthorize }),
  // уже 1100 px список участников ложится поверх чата (на телефоне — на весь экран), сам он не открывается
  showMembers: isWideNow(),
  toggleMembers: () => set((s) => ({ showMembers: !s.showMembers })),
  voiceChatChannelId: initialLink?.channelId ?? null,
  setVoiceChatChannelId: (voiceChatChannelId) => set({ voiceChatChannelId }),
  searchQuery: null,
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  dmSearch: null,
  setDMSearch: (dmSearch) => set({ dmSearch }),
  threadsPanel: null,
  setThreadsPanel: (threadsPanel) => set({ threadsPanel }),
  // ссылка на сообщение: окно вокруг него начинает грузиться сразу, не дожидаясь READY, —
  // иначе первая загрузка канала успевает раньше и перетирает его последними сообщениями
  jumpTarget: initialLink?.messageId ? { channelId: initialLink.channelId, messageId: initialLink.messageId } : null,
  setJumpTarget: (jumpTarget) => set(jumpTarget ? { jumpTarget, voiceChatChannelId: jumpTarget.channelId } : { jumpTarget }),
  pendingLink: initialLink,
  setPendingLink: (pendingLink) => set({ pendingLink }),
  linkDenied: false,
  setLinkDenied: (linkDenied) => set({ linkDenied }),
  collapsed: {},
  toggleCategory: (id) => set((s) => ({ collapsed: { ...s.collapsed, [id]: !s.collapsed[id] } })),
  gateway: 'idle',
  setGateway: (gateway) => set({ gateway }),
  currentUser: null,
  setCurrentUser: (currentUser) => set({ currentUser }),
  settings: null,
  openSettings: (section = 'account') => set(section === 'moderation' ? { settings: null, moderationOpen: true } : { settings: section }),
  closeSettings: () => set({ settings: null }),
  }
})

/*
 * Окно сузили — участники закрываются, а не ложатся поверх чата сами; расширили — возвращаются,
 * если были открыты на широком. Открытое поверх чата на узком на широкое не переносится.
 */
let wideMembers = true
onWideChange((wide) => {
  if (wide) {
    useAppStore.setState({ showMembers: wideMembers })
    return
  }
  wideMembers = useAppStore.getState().showMembers
  useAppStore.setState({ showMembers: false })
})
